import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  activateTrackingObject,
  deactivateTrackingObject,
  getTrackingObject,
  getTrackingObjectConfig,
  getTrackingObjectStatus,
  listTrackingObjects,
  moveTrackingObject,
  updateTrackingObjectConfig,
} from './api/api'
import {
  trackingObjectConfigKey,
  trackingObjectDetailKey,
  trackingObjectsListKey,
  trackingObjectStatusKey,
} from './keyFactory'
import type { ActivateTrackingObjectRequest, TrackingObjectConfig } from './types'

export function useTrackingObjects(customerId: string) {
  return useQuery({
    queryKey: trackingObjectsListKey(customerId),
    queryFn: () => listTrackingObjects(customerId),
  })
}

export function useTrackingObject(customerId: string, tid: string) {
  return useQuery({
    queryKey: trackingObjectDetailKey(customerId, tid),
    queryFn: () => getTrackingObject(customerId, tid),
  })
}

// Invalidates BOTH the source and target customers' lists (plan.md §9) — the
// object leaves one list and appears in the other; no hand-rolled cache edits.
export function useMoveTrackingObject(customerId: string, tid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (targetCid: string) => moveTrackingObject(customerId, tid, targetCid),
    onSuccess: (_object, targetCid) => {
      queryClient.invalidateQueries({ queryKey: trackingObjectsListKey(customerId) })
      queryClient.invalidateQueries({ queryKey: trackingObjectsListKey(targetCid) })
    },
  })
}

// Polls every 30s — an improvement over the mobile one-shot fetch (plan.md §3.6).
export function useTrackingObjectStatus(customerId: string, tid: string) {
  return useQuery({
    queryKey: trackingObjectStatusKey(customerId, tid),
    queryFn: () => getTrackingObjectStatus(customerId, tid),
    refetchInterval: 30_000,
  })
}

export function useTrackingObjectConfig(customerId: string, tid: string) {
  return useQuery({
    queryKey: trackingObjectConfigKey(customerId, tid),
    queryFn: () => getTrackingObjectConfig(customerId, tid),
  })
}

// Whole-document write; invalidate so the tabs re-read the saved config.
export function useUpdateTrackingObjectConfig(customerId: string, tid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (config: TrackingObjectConfig) => updateTrackingObjectConfig(customerId, tid, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trackingObjectConfigKey(customerId, tid) })
    },
  })
}

// Invalidates the list so a freshly-activated object shows up on the refetch
// (plan.md §3.4 — no hand-rolled cache surgery).
export function useActivateTrackingObject(customerId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (req: ActivateTrackingObjectRequest) => activateTrackingObject(customerId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trackingObjectsListKey(customerId) })
    },
  })
}

// Invalidates the list rather than the mobile's hand-rolled cache filtering
// (plan.md §3.4) — the deactivated object disappears on the refetch.
export function useDeactivateTrackingObject(customerId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (tid: string) => deactivateTrackingObject(customerId, tid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trackingObjectsListKey(customerId) })
    },
  })
}
